import { useEffect } from 'react'
import { z } from 'zod'

import {
  setParsedStorageValue,
  useParsedStorage,
} from '../../../core/hooks/useParsedStorage'
import { useCurrentUser } from '../../utils/currentUserContext'
import type { AgentSessionConversation } from './AgentSessionQueries'
import { useAgentSessionsQuery } from './AgentSessionQueries'

const VIEWED_AT_STORAGE_KEY_PREFIX = 'joyful-agent-session-viewed-at'
const UNREAD_WINDOW_MS = 24 * 60 * 60 * 1000
const VIEWED_AT_RETENTION_MS = 2 * UNREAD_WINDOW_MS

const ViewedAtByConversationIdSchema = z.record(z.string(), z.iso.datetime())
type ViewedAtByConversationId = z.infer<typeof ViewedAtByConversationIdSchema>

const EMPTY_VIEWED_AT_BY_CONVERSATION_ID: ViewedAtByConversationId = {}

/** Stable across renders so `useParsedStorage` can cache its parsed snapshot. */
const VIEWED_AT_STORAGE_PARAMS = {
  initialValue: EMPTY_VIEWED_AT_BY_CONVERSATION_ID,
  schema: ViewedAtByConversationIdSchema,
  strategy: 'local',
} as const

export function useIsAgentSessionUnread() {
  const storageKey = useViewedAtStorageKey()
  const [viewedAtByConversationId] = useParsedStorage(
    storageKey,
    VIEWED_AT_STORAGE_PARAMS,
  )

  return (conversation: AgentSessionConversation) => {
    if (conversation.current_user_participated !== true) {
      return false
    }

    if (isSettledWithinUnreadWindow(conversation) === false) {
      return false
    }

    const viewedAt = viewedAtByConversationId[conversation.id]
    return viewedAt === undefined || viewedAt < conversation.modified_at
  }
}

export function useHasUnreadEncounterAgentSession(encounterId: string) {
  const isAgentSessionUnread = useIsAgentSessionUnread()
  const sessionsQuery = useAgentSessionsQuery({
    refetchInterval: 5_000,
    source_id: encounterId,
    source_type: 'encounter',
    staleTime: 0,
  })

  return (sessionsQuery.data ?? []).some((conversation) => {
    return isAgentSessionUnread(conversation)
  })
}

/**
 * Records the conversation as read for as long as it is on screen. Keyed on
 * `modified_at`, so a run that settles while the user is watching never badges.
 */
export function useMarkAgentSessionViewed(
  conversation: Pick<AgentSessionConversation, 'id' | 'modified_at'> | null,
) {
  const storageKey = useViewedAtStorageKey()
  const conversationId = conversation?.id ?? null
  const modifiedAt = conversation?.modified_at ?? null

  useEffect(() => {
    if (conversationId === null || modifiedAt === null) {
      return
    }

    setParsedStorageValue(
      storageKey,
      (currentViewedAtByConversationId) => {
        return pruneExpiredViewedAt({
          ...currentViewedAtByConversationId,
          [conversationId]: modifiedAt,
        })
      },
      VIEWED_AT_STORAGE_PARAMS,
    )
  }, [conversationId, modifiedAt, storageKey])
}

function useViewedAtStorageKey() {
  const currentUser = useCurrentUser()
  return `${VIEWED_AT_STORAGE_KEY_PREFIX}-${currentUser.id}`
}

function isSettledWithinUnreadWindow(conversation: AgentSessionConversation) {
  const status = conversation.latest_run_status
  if (status !== 'awaiting_human' && status !== 'failed') {
    return false
  }

  const settledAt = new Date(conversation.modified_at).getTime()
  return settledAt > Date.now() - UNREAD_WINDOW_MS
}

/**
 * Markers older than the unread window can never make a session read again, so
 * dropping them on every write keeps the stored map from growing without bound.
 */
function pruneExpiredViewedAt(
  viewedAtByConversationId: ViewedAtByConversationId,
): ViewedAtByConversationId {
  const oldestRetainedAt = new Date(
    Date.now() - VIEWED_AT_RETENTION_MS,
  ).toISOString()

  return Object.fromEntries(
    Object.entries(viewedAtByConversationId).filter(([, viewedAt]) => {
      return viewedAt > oldestRetainedAt
    }),
  )
}
